import * as React from 'react';

import { useAnalytics } from '@/lib/analytics/hooks/use-analytics';
import { Button } from '@/ui/button';
import { cn } from '@/utils';

import OrientationIcon from '../../assets/orientation.svg';

import { OrientationDrawer } from './orientation-drawer';

/**
 * Card title for Dashboard Orientation notification
 */
const ORIENTATION_CARD_TITLE = 'Dashboard Orientation';

/**
 * Card subtitle for Dashboard Orientation notification
 */
const ORIENTATION_CARD_SUBTITLE = 'Get the tour of the dashboard';

/**
 * Alt text for Orientation card icon
 */
const ORIENTATION_ICON_ALT = 'Dashboard orientation icon';

/**
 * Props for OrientationCard component
 * Currently accepts no props, but interface is defined for future extensibility
 */

type OrientationCardProps = Record<string, unknown>;

/**
 * Dashboard Orientation notification card
 *
 * Displays a notification card for dashboard orientation tour.
 * Clicking "Start" opens a drawer with a YouTube video tutorial.
 *
 * @example
 * ```tsx
 * <OrientationCard ref={cardRef} />
 * ```
 */
export const OrientationCard = React.forwardRef<
  HTMLDivElement,
  OrientationCardProps
>((_, ref) => {
  const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);
  const { trackEvent } = useAnalytics();

  const handleClick = React.useCallback(() => {
    trackEvent('DashboardOrientationClickedStart', 1, {
      clickType: 'cta',
      clickCTA: 'Start',
      clickSection: 'Dashboard Orientation Section',
    });
    setIsDrawerOpen(true);
  }, [trackEvent]);

  const handleCloseDrawer = React.useCallback(() => {
    setIsDrawerOpen(false);
  }, []);

  return (
    <>
      <div
        ref={ref}
        role="article"
        aria-labelledby="orientation-card-title"
        aria-describedby="orientation-card-description"
        className={cn(
          'bg-white/60 rounded-[14px] p-4',
          'flex items-center gap-4',
          'transition-all duration-200',
          'hover:shadow-lg'
        )}
      >
        {/* Icon Container */}
        <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center">
          <img
            src={OrientationIcon}
            alt={ORIENTATION_ICON_ALT}
            className="w-12 h-12"
          />
        </div>

        {/* Content */}
        <div className="flex-1">
          <h3
            id="orientation-card-title"
            className="text-sm font-semibold text-gray-900"
          >
            {ORIENTATION_CARD_TITLE}
          </h3>
          <p
            id="orientation-card-description"
            className="text-xs text-gray-600 mt-0.5"
          >
            {ORIENTATION_CARD_SUBTITLE}
          </p>
        </div>

        {/* Action Button */}
        <Button
          variant="outline"
          size="sm"
          onClick={handleClick}
          className="flex-shrink-0"
        >
          Start
        </Button>
      </div>

      {/* Drawer */}
      <OrientationDrawer isOpen={isDrawerOpen} onClose={handleCloseDrawer} />
    </>
  );
});

OrientationCard.displayName = 'OrientationCard';
